/**
 * Eén manier om bedragen te tonen, overal op de site.
 * Hele bedragen zonder centen (€ 45), anders met komma (€ 5,95).
 */
const wholeFmt = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 })
const centsFmt = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Met punt voor duizendtallen: € 1.250 en € 1.250,50 (niet € 1250). */
export function euro(amount: number, opts: { cents?: boolean } = {}): string {
  const showCents = opts.cents ?? !Number.isInteger(amount)
  const value = showCents ? centsFmt.format(amount) : wholeFmt.format(Math.round(amount))
  return `€ ${value}`
}

/** Euro's, altijd met centen: totalen in mand en checkout staan zo netjes onder elkaar. */
export const euroCents = (amount: number) => euro(amount, { cents: true })

/** Rating als Nederlandse notatie: 4.8 → "4,8". */
export function rating(value: number): string {
  return value.toFixed(1).replace('.', ',')
}

// ——— Rekenen met geld ———
// Altijd in hele centen rekenen en pas bij het tonen terug naar euro's.
// `0.1 + 0.2` is 0.30000000000000004; `1.005 * 100` is 100.49999999999999.

/**
 * Euro's → hele centen, zonder de klassieke afrondingsfout: `1.005` wordt 101, niet 100.
 * Schuift de komma via de decimale notatie in plaats van te vermenigvuldigen.
 * Geen eindig getal → RangeError: een prijs van NaN mag nooit stil € 0 worden.
 */
export function toCents(amount: number): number {
  if (!Number.isFinite(amount)) throw new RangeError(`Ongeldig bedrag: ${amount}`)
  const shifted = Number(`${amount}e2`)
  return Math.round(Number.isFinite(shifted) ? shifted : amount * 100)
}

/**
 * Wat iemand in een bedragveld typt → hele centen, of `null` als het geen bedrag is.
 * `,` is de decimaal. Een `.` met precies 1 of 2 cijfers erachter ook ("12.50"). Een `.` als
 * duizendtal alleen in groepen van 3 ("1.000", "1.000,50"). "€" en spaties tellen niet mee.
 * Meer dan 2 decimalen → `null`. Rekent met tekst, niet met `* 100`, dus zonder afrondingsfout.
 */
export function parseEuroInput(text: string): number | null {
  const clean = text.replace(/€/g, '').replace(/\s+/g, '')
  let whole: string
  let frac = ''
  const comma = clean.match(/^(\d{1,3}(?:\.\d{3})+|\d+),(\d{1,2})$/)
  const dotDecimal = clean.match(/^(\d+)\.(\d{1,2})$/)
  if (comma) {
    whole = comma[1]
    frac = comma[2]
  } else if (dotDecimal) {
    whole = dotDecimal[1]
    frac = dotDecimal[2]
  } else if (/^\d{1,3}(?:\.\d{3})+$/.test(clean) || /^\d+$/.test(clean)) {
    whole = clean
  } else {
    return null
  }
  return Number(`${whole.replace(/\./g, '')}${frac.padEnd(2, '0')}`)
}

/** Hele centen → euro's. */
export const fromCents = (cents: number) => cents / 100

/** Hele centen → "€ 1.250,00", altijd met centen. Voor bedragen van de server (beheer, kassa, bonnen). */
export const money = (cents: number) => euro(fromCents(cents), { cents: true })

/** Som van regels (prijs × aantal) in hele centen. */
export const sumCents = (lines: readonly { price: number; qty: number }[]) =>
  lines.reduce((total, l) => total + toCents(l.price) * l.qty, 0)
